// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';
import { normalizarBusca } from '@/nucleo/utilitarios';

import type { FiltroDeSolicitacoes, OpcaoDeRecurso, SolicitacaoDeServico } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados das solicitações de serviço.
 *
 * Mesmo desenho das demais telas do módulo: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa. A diferença para o
 * padrão real (ex. `operacao/condutores/dados.ts`) é o `queryFn`, que resolve
 * dado local em vez de chamar `chamarApi(...)` — ver a justificativa em
 * `comum/mock.ts`.
 *
 * ESTE MÓDULO É SÓ LEITURA. Não há API de solicitações de serviço: abrir e
 * cancelar solicitação mostram o aviso de indisponibilidade na tela
 * (`NovaSolicitacaoPage.tsx`, `SolicitacaoDetailPage.tsx`) e não mexem nesta
 * lista, nem em memória — ver o bloco no fim deste arquivo.
 */

const SOLICITACOES = dadosMock.solicitacoes as SolicitacaoDeServico[];

const RECURSOS = dadosMock.recursos as OpcaoDeRecurso[];

export const CHAVES_DE_SOLICITACOES = {
  lista: (filtro: FiltroDeSolicitacoes) =>
    ['credenciado', 'solicitacoes', 'lista', filtro] as const,
  detalhe: (id: string) => ['credenciado', 'solicitacoes', 'detalhe', id] as const,
  recursos: ['credenciado', 'solicitacoes', 'recursos'] as const,
};

function aplicarFiltro(
  itens: SolicitacaoDeServico[],
  filtro: FiltroDeSolicitacoes,
): SolicitacaoDeServico[] {
  const busca = filtro.busca ? normalizarBusca(filtro.busca) : '';
  return itens.filter((item) => {
    if (filtro.tipo && item.tipo !== filtro.tipo) return false;
    if (filtro.status && item.status !== filtro.status) return false;
    if (busca) {
      const alvo = normalizarBusca(`${item.protocolo} ${item.motivo}`);
      if (!alvo.includes(busca)) return false;
    }
    if (filtro.criadaDe && new Date(item.aberta_em) < new Date(`${filtro.criadaDe}T00:00:00`))
      return false;
    if (filtro.criadaAte && new Date(item.aberta_em) > new Date(`${filtro.criadaAte}T23:59:59`))
      return false;
    return true;
  });
}

function ordenarPorAberturaDesc(itens: SolicitacaoDeServico[]): SolicitacaoDeServico[] {
  return [...itens].sort(
    (a, b) => new Date(b.aberta_em).getTime() - new Date(a.aberta_em).getTime(),
  );
}

export function useSolicitacoes(
  filtro: FiltroDeSolicitacoes,
): UseQueryResult<Pagina<SolicitacaoDeServico>, Error> {
  return useQuery<Pagina<SolicitacaoDeServico>, Error>({
    queryKey: CHAVES_DE_SOLICITACOES.lista(filtro),
    queryFn: () =>
      resolverMock(
        paginar(
          ordenarPorAberturaDesc(aplicarFiltro(SOLICITACOES, filtro)),
          filtro.pagina,
          filtro.tamanho,
        ),
      ),
  });
}

export function useSolicitacao(
  id: string | undefined,
): UseQueryResult<SolicitacaoDeServico | null, Error> {
  return useQuery<SolicitacaoDeServico | null, Error>({
    queryKey: CHAVES_DE_SOLICITACOES.detalhe(id ?? ''),
    queryFn: () => resolverMock(SOLICITACOES.find((item) => item.id === id) ?? null),
    enabled: !!id,
  });
}

export function useRecursosDisponiveis(): UseQueryResult<OpcaoDeRecurso[], Error> {
  return useQuery<OpcaoDeRecurso[], Error>({
    queryKey: CHAVES_DE_SOLICITACOES.recursos,
    queryFn: () => resolverMock(RECURSOS),
  });
}

/*
 * NÃO EXISTE GANCHO DE GRAVAÇÃO DE SOLICITAÇÃO, E ISSO É DELIBERADO.
 *
 * Até 2026-10-02 `useCriarSolicitacao` punha a solicitação nova no topo de um
 * array em memória, com protocolo gerado, e `useCancelarSolicitacao` trocava o
 * status para "cancelada": a lista e o detalhe mostravam o que ninguém gravou,
 * e tudo sumia no recarregamento. É fingir gravar, o que o dono
 * proibiu em 2026-10-02 (D-E) e o `CLAUDE.md` já proibia na exceção de mock:
 * "Só leitura. Nunca em 'Salvar' nem em ação em lote — essas continuam
 * mostrando o aviso de indisponibilidade, nunca fingindo gravar".
 *
 * Quando a API de solicitações existir, os ganchos voltam chamando `chamarApi`
 * de verdade, e as telas trocam o aviso pela mutação no mesmo commit.
 */
